import { boxSubdomain } from '@lub0v/santa-utils';
interface AnalyticsPayload {
  url?: string;
  [key: string]: unknown;
}
declare global {
  interface Window {
    __ssBoxDomains?: string[];
    umami?: { track: (event: string, data?: Record<string, unknown>) => unknown };
    umamiBeforeSend?: (type: string, payload: AnalyticsPayload) => AnalyticsPayload | false;
  }
}

const SITES = {
  'santa-secret.ru': {
    id: '8bea7175-7efe-4a6f-ab77-e8161f17bb00',
    script: 'https://analytics.santa-secret.ru/script.js',
  },
  'santa-secret.com': {
    id: '6da1e0b8-3c56-4d7f-940a-a6490e917dda',
    script: 'https://analytics.santa-secret.com/script.js',
  },
};
function productionSite() {
  const host = window.location.hostname.replace(/^www\./, '');
  if (host === 'santa-secret.ru' || host === 'santa-secret.com') return host;
  // Only server-configured production aliases; previews never enter these sites.
  return (
    (['santa-secret.ru', 'santa-secret.com'] as const).find(
      (base) => window.__ssBoxDomains?.includes(base) && boxSubdomain(host, [base]) !== null,
    ) ?? null
  );
}
const redactKeys = new Set([
  'a',
  'email',
  'token',
  'notification',
  'join',
  'code',
  'session_id',
  'password',
  'redirect',
  'error',
]);
/** Preserve the legacy before-send filter so invitation credentials are not sent. */
export function redactAnalyticsUrl(raw: string): string {
  try {
    const absolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw);
    const url = new URL(raw, 'https://umami.invalid');
    for (const key of [...url.searchParams.keys()]) {
      if (redactKeys.has(key.toLowerCase())) url.searchParams.set(key, '...');
    }
    if (url.pathname === '/recover/access' && url.hash) url.hash = '#...';
    const suffix = url.pathname + url.search + url.hash;
    return absolute ? url.origin + suffix : suffix;
  } catch {
    return raw;
  }
}
const pendingEvents: { event: string; data?: Record<string, unknown> }[] = [];
export function initUmami(): void {
  const site = productionSite();
  if (!site) return;
  const config = SITES[site];
  window.umamiBeforeSend = (_type, payload) => {
    if (!productionSite()) return false;
    return payload?.url ? { ...payload, url: redactAnalyticsUrl(payload.url) } : payload;
  };
  if ([...document.scripts].some((script) => script.src === config.script)) return;
  const script = document.createElement('script');
  script.src = config.script;
  script.defer = true;
  script.dataset.websiteId = config.id;
  script.dataset.beforeSend = 'umamiBeforeSend';
  script.dataset.domains = window.location.hostname;
  script.onload = () => {
    for (const { event, data } of pendingEvents.splice(0)) window.umami?.track(event, data);
  };
  document.head.appendChild(script);
}
export function trackUmamiEvent(event: string, data?: Record<string, unknown>): unknown {
  if (!productionSite()) return;
  if (window.umami) return window.umami.track(event, data);
  else {
    if (pendingEvents.length < 30) pendingEvents.push({ event, data });
    initUmami();
  }
}
